import { Button, Checkbox, Input, InputAdornment, MenuItem, Pagination, Select, styled } from "@mui/material";
import MUITooltip from "@mui/material/Tooltip";
import { DataGrid, QuickFilter, QuickFilterControl } from "@mui/x-data-grid";
import includes from "lodash/includes";
import { useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { Comments, Doc, Landvision, Search } from "../../../common/js/icons";
import utils, { calculateDaysBetween, formatPrice, navigateToLandvision } from "../utils/operations";
import { NO_CONTENT_PLACEHOLDER } from "./constants";

const StyledLb = styled(Link)({
    textDecoration: "none",
});

const StyledPaginationContainer = styled("div")({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "10px",
    gap: "16px",
});

const StyledPageInfo = styled("div")({
    color: "#666",
    fontSize: "14px",
});

const StyledListingIdLink = styled("span")({
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
    color: "#2C99D2",
    fontWeight: "600",
    display: "-webkit-box",
    WebkitLineClamp: "2",
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
});

const StyledAlertNotificationText = styled("div")({
    color: "red",
    fontSize: "13px",
    fontWeight: "bold",
});

const StyledDataGrid = styled(DataGrid)({
    "& .MuiDataGrid-columnSeparator": {
        display: "none",
    },
    "& .MuiDataGrid-cell": {
        display: "flex",
        alignItems: "center",
        alignContent: "left",
        borderBottom: "1px solid rgb(212, 212, 212)",
        fontSize: "13px",
        padding: "0.75em 0",
        overflowWrap: "break-word",
        overflow: "hidden",
    },
    "& .MuiDataGrid-columnHeaderTitle": {
        color: "rgb(158, 158, 158)",
        fontSize: "12px",
        fontWeight: 600,
        textTransform: "uppercase",
        textAlign: "left",
    },
    "& .MuiDataGrid-columnHeaderTitleContainerContent": {
        width: "100%",
    },
    "& .MuiDivider-root": {
        display: "none",
    },
    border: "none",
});

const StyledLightButton = styled(Button)({
    fontWeight: "400",
    minWidth: "0px",
    margin: "0px",
    marginRight: "5px",
    padding: "5px 10px",
});

const StyledSelectTitle = styled(Select)({
    fontWeight: "600",
    color: "rgb(158, 158, 158)",
    fontSize: "12px",
    height: "20px",
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
    "& .MuiSelect-select": { padding: 0 },
    paddingLeft: "2px",
});

const StyledSelectSpan = styled("span")({
    color: "rgb(158, 158, 158)",
    fontSize: "12px",
    fontWeight: "600",
});

const BrokerActiveListingsToolbar = () => {
    const container = document.getElementById("listingTabsLine");
    if (!container) return null;
    return createPortal(
        <QuickFilter defaultExpanded>
            <QuickFilterControl
                render={({ ref, ...controlProps }, state) => (
                    <Input
                        {...controlProps}
                        placeholder="Search Listings"
                        inputRef={ref}
                        sx={{ fontSize: 14, padding: "4px 6px" }}
                        disableUnderline={true}
                        startAdornment={
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        }
                    />
                )}
            />
        </QuickFilter>,
        container
    );
};

const BrokerActiveListingsTable = (props) => {
    const {
        selectAll,
        handleSelectAll,
        listings,
        selectedListings,
        handleCheckboxClick,
        navigateToRequestUpdate,
        currentPath,
        role,
        openCloseDealDialog,
    } = props;

    const listingsLocal = listings?.broker_open;
    const [sortListingOrAddress, setSortListingOrAddress] = useState("Address");

    const totalCount = listingsLocal?.length ?? 0;
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 50;
    const totalPages = Math.ceil(totalCount / pageSize);

    const navigate = useNavigate();
    const handleRequestUpdate = (listing) => {
        navigateToRequestUpdate(currentPath, listing);
        navigate(`/listing/${listing.listingKey}`);
    };

    const brokersTooltip = (listingBrokers) => {
        return listingBrokers
            .filter((listingBroker) => (listingBroker?.brokerAgentKey?.person ?? null) !== null)
            .map(
                (listingBroker) =>
                    (listingBroker?.brokerAgentKey?.person?.firstName ?? "") +
                    " " +
                    (listingBroker?.brokerAgentKey?.person?.lastName ?? "")
            )
            .join(", ");
    };

    const displayAlertNotification = (listing) => {
        const alert =
            (listing?.status ?? null) === "Active Broker Action Required" ? (
                <StyledAlertNotificationText>Update Action Required! (See Comments)</StyledAlertNotificationText>
            ) : (listing?.status ?? null) === "Update Requested" ? (
                <StyledAlertNotificationText style={{ color: "#FCBA03" }}>Update Requested</StyledAlertNotificationText>
            ) : (
                checkExpiration(listing)
            );
        return alert;
    };

    const checkExpiration = (listing) => {
        if ((listing?.autoRenewFlg ?? false) !== true) {
            const daysBetween = calculateDaysBetween(new Date(listing?.expirationDate));
            if ((listing?.expired ?? false) === true) {
                return <StyledAlertNotificationText>Expired</StyledAlertNotificationText>;
            } else if (daysBetween < 61) {
                return (
                    <StyledAlertNotificationText>
                        Expires in {daysBetween + 1} {daysBetween === 0 ? `day` : `days`}
                    </StyledAlertNotificationText>
                );
            }
        }
    };

    const findPriceOrRate = (listing, isPriceString = true) => {
        return (listing?.askingRentMax == null || listing?.askingRentMax === 0) &&
            (listing?.listPrice == null || listing?.listPrice === 0)
            ? isPriceString
                ? "Call Broker"
                : 0
            : listing?.listingType.toLowerCase() === "lease"
              ? includes(["Office", "Retail", "Healthcare", "Life Science"], listing?.useType) &&
                (includes(["WA", "OR", "AZ"], listing?.property?.primaryAddress?.city?.state?.stateCode) ||
                    (listing?.property?.primaryAddress?.city?.city ?? {}) === "San Francisco")
                  ? isPriceString
                      ? `$${listing?.baseRentPerYear.toFixed(2)} /SF/YR`
                      : listing?.baseRentPerYear
                  : isPriceString
                    ? `$${listing?.askingRentMax.toFixed(2)} /SF/MTH`
                    : listing?.askingRentMax
              : isPriceString
                ? `$${
                      (listing?.listPrice ?? null) !== null
                          ? formatPrice(listing?.listPrice ?? 0)
                          : NO_CONTENT_PLACEHOLDER
                  }`
                : (listing?.listPrice ?? 0);
    };

    const handlePageChange = (_, value) => {
        setCurrentPage(value);
    };

    const columns = [
        {
            field: "checkbox",
            headerName: "",
            sortable: false,
            disableColumnMenu: true,
            disableColumnSort: true,
            width: 50,
            headerAlign: "center",
            align: "center",
            getApplyQuickFilterFn: null,
            renderHeader: () => {
                return (
                    <Checkbox
                        className={selectAll ? "listingCheckboxAllSelected" : "listingCheckboxAll"}
                        checked={selectAll}
                        value={selectAll}
                        onChange={() => handleSelectAll()}
                        color="primary"
                    />
                );
            },
            renderCell: (params) => {
                const listing = params.row.listing;
                return (
                    <Checkbox
                        className={
                            selectedListings.get(listing.listingKey) === true
                                ? "listingCheckboxAllSelected"
                                : "listingCheckboxAll"
                        }
                        checked={selectedListings.get(listing.listingKey) === true}
                        value={selectedListings.get(listing.listingKey) === true}
                        onChange={() => handleCheckboxClick(listing.listingKey, "broker_open")}
                        color="primary"
                    />
                );
            },
        },
        {
            field: "primaryPhotoUrl",
            headerName: "Property",
            sortable: false,
            disableColumnMenu: true,
            disableColumnSort: true,
            flex: 0.7,
            getApplyQuickFilterFn: null,
            renderCell: (params) => {
                const listing = params.row.listing;
                const propertyPhoto =
                    listing?.listing_photo != null ? listing?.listing_photo : listing?.primaryPhotoUrl;
                const isKmManaged = listings?.property?.kmManagedFlg ?? false;
                return (
                    <div
                        className="propertySmallImageContainer"
                        style={{
                            backgroundImage: `url(${propertyPhoto})`,
                            width: "85px",
                            minWidth: "85px",
                            height: "85px",
                        }}
                    >
                        {isKmManaged && <span className="listingTag">KM Managed</span>}
                    </div>
                );
            },
        },
        {
            field: "address",
            disableColumnMenu: true,
            flex: 1.4,
            renderHeader: (params) => {
                return (
                    <div>
                        <span className="MuiDataGrid-columnHeaderTitle">Address</span>
                        <div style={{}}>
                            <StyledSelectSpan variant="standard">Sort By: </StyledSelectSpan>
                            <StyledSelectTitle
                                value={sortListingOrAddress}
                                onChange={(e) => setSortListingOrAddress(e.target.value)}
                                disableUnderline
                            >
                                <MenuItem value={"Address"}>Address</MenuItem>
                                <MenuItem value={"Listing Name"}>Listing Name</MenuItem>
                            </StyledSelectTitle>
                        </div>
                    </div>
                );
            },
            getApplyQuickFilterFn: (value) => {
                return (cellValue, row) => {
                    return (
                        cellValue.toLowerCase().includes(value.toLowerCase()) ||
                        row?.listing?.listingName.toLowerCase().includes(value.toLowerCase())
                    );
                };
            },
            valueGetter: (value, row) => {
                const listing = row.listing;
                if (sortListingOrAddress === "Address") {
                    return `${listing?.property?.primaryAddress?.address1 || ""} ${
                        listing?.property?.primaryAddress?.city?.city || ""
                    }, ${listing?.property?.primaryAddress?.city?.state?.stateCode || ""}, ${
                        listing?.property?.primaryAddress?.zipCode?.zipCode || ""
                    }`;
                } else {
                    return listing?.listingName.toLowerCase();
                }
            },
            renderCell: (params) => {
                const listing = params.row.listing;
                return (
                    <div>
                        <StyledLb to={`/listing/${listing.listingKey}`} target="_blank">
                            <StyledListingIdLink style={listing.expired ? { color: "red" } : { color: "#2C99D2" }}>
                                {`${listing?.listingName ?? "Default"}`.trim()}
                            </StyledListingIdLink>
                        </StyledLb>
                        <div>{`${listing?.property?.primaryAddress?.address1 ?? {}}`}</div>
                        <div>{`${listing?.property?.primaryAddress?.city?.city ?? {}}, ${
                            listing?.property?.primaryAddress?.city?.state?.stateCode ?? {}
                        }, ${listing?.property?.primaryAddress?.zipCode?.zipCode ?? {}}`}</div>
                        {displayAlertNotification(listing)}
                    </div>
                );
            },
        },
        {
            field: "brokers",
            headerName: "Brokers",
            disableColumnMenu: true,
            flex: 0.833,
            getApplyQuickFilterFn: (value) => {
                return (cellValue, row) => {
                    return (
                        cellValue.toLowerCase().includes(value.toLowerCase()) ||
                        row?.listing?.listingBrokers?.some((broker) => {
                            return (
                                broker.brokerAgentKey?.person?.firstName +
                                " " +
                                broker?.brokerAgentKey?.person?.lastName
                            )
                                .toLowerCase()
                                .includes(value.toLowerCase());
                        })
                    );
                };
            },
            valueGetter: (value, row) => {
                const listing = row.listing;
                return listing?.listingBrokers?.length === 1
                    ? `${listing.listingBrokers[0]?.brokerAgentKey?.person?.firstName || ""} ${
                          listing.listingBrokers[0]?.brokerAgentKey?.person?.lastName || ""
                      }`.trim() ||
                          listing?.listingAgent ||
                          ""
                    : listing?.listingBrokers?.length > 1
                      ? `${listing.listingBrokers.length} Brokers`
                      : listing?.listingAgent || "";
            },
            renderCell: (params) => {
                const listing = params.row.listing;
                return (
                    <div>
                        {(listing?.listingBrokers ?? null) !== null ? (
                            (listing?.listingBrokers ?? []).length > 1 ? (
                                <MUITooltip title={brokersTooltip(listing?.listingBrokers)} placement="top">
                                    <span>{listing?.listingBrokers?.length + " Brokers"}</span>
                                </MUITooltip>
                            ) : (
                                <div>{brokersTooltip(listing?.listingBrokers ?? "")}</div>
                            )
                        ) : (
                            <div>{listing?.listingAgent ?? ""}</div>
                        )}
                    </div>
                );
            },
        },
        {
            field: "type",
            headerName: "Type",
            disableColumnMenu: true,
            flex: 0.833,
            valueGetter: (value, row) => {
                const listing = row?.listing;
                return `${listing?.attributes?.useType ?? ""} ${listing?.listingType ?? ""}`;
            },
            renderCell: (params) => {
                const listing = params.row.listing;
                return <div>{`${listing?.attributes?.useType ?? ""} ${listing?.listingType ?? ""}`}</div>;
            },
        },
        {
            field: "askingRentMax",
            headerName: "Rate/Price",
            disableColumnMenu: true,
            flex: 0.833,
            valueGetter: (value, row) => {
                return findPriceOrRate(row.listing, false);
            },
            renderCell: (params) => {
                return <div>{params.row.askingRentMax}</div>;
            },
        },
        {
            field: "sfAvailable",
            headerName: "SQFT",
            disableColumnMenu: true,
            flex: 0.833,
            valueGetter: (value, row) => {
                return row.listing?.sfAvail ?? null;
            },
            renderCell: (params) => {
                const listing = params.row.listing;
                return <div>{listing?.sfAvail != null ? utils.toSqft(listing?.sfAvail) : NO_CONTENT_PLACEHOLDER}</div>;
            },
        },
        {
            field: "dateAvailable",
            headerName: "Date Avail",
            disableColumnMenu: true,
            flex: 0.833,
            valueGetter: (value, row) => {
                return row.listing?.dateAvailable ?? null;
            },
            renderCell: (params) => {
                const listing = params.row.listing;
                return (
                    <div>
                        {listing?.dateAvailable != null
                            ? utils.formatDate("MM-DD-YYYY")(listing?.dateAvailable)
                            : NO_CONTENT_PLACEHOLDER}
                    </div>
                );
            },
            sortComparator: (v1, v2) =>
                new Date(utils.formatDate("YYYY-MM-DD")(v1)).getTime() -
                new Date(utils.formatDate("YYYY-MM-DD")(v2)).getTime(),
        },
        {
            field: "links",
            headerName: "Links",
            sortable: false,
            disableColumnMenu: true,
            disableColumnSort: true,
            flex: 1.667,
            getApplyQuickFilterFn: null,
            renderCell: (params) => {
                const listing = params.row.listing;
                const isKmListing = !(listing?.nonKmListingFlg ?? null);
                const viewCloseButton =
                    isKmListing && includes(["broker", "portlandBrokerAdmin", "arizonaBrokerAdmin", "evp"], role);
                return (
                    <div style={{ whiteSpace: "nowrap" }}>
                        <MUITooltip title="Flyer" placement="top">
                            <span>
                                <StyledLightButton
                                    disabled={listing?.flyerUrl == null}
                                    onClick={() => window.open(listing?.flyerUrl, "_blank")}
                                    color="primary"
                                >
                                    <Doc fill={listing?.flyerUrl != null ? "#2C99D2" : "#9E9E9E"} />
                                </StyledLightButton>
                            </span>
                        </MUITooltip>

                        <MUITooltip title="Request Update" placement="top">
                            <span>
                                <StyledLightButton onClick={() => handleRequestUpdate(listing)} color="primary">
                                    <Comments
                                        fill={
                                            listing?.status === "Active Broker Action Required"
                                                ? "red"
                                                : listing?.status === "Update Requested"
                                                  ? "#FCBA03"
                                                  : "#2C99D2"
                                        }
                                    />
                                </StyledLightButton>
                            </span>
                        </MUITooltip>

                        <MUITooltip title="Landvision" placement="top">
                            <span>
                                <StyledLightButton
                                    onClick={() =>
                                        navigateToLandvision(listing?.property?.propertyKey, listing.listingKey)
                                    }
                                    color="primary"
                                >
                                    <Landvision fill="#2C99D2" />
                                </StyledLightButton>
                            </span>
                        </MUITooltip>

                        {viewCloseButton && (
                            <MUITooltip title="Close Deal" placement="top">
                                <span>
                                    <span data-tooltip-id="closeDeal">
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            onClick={() => openCloseDealDialog(listing)}
                                        >
                                            Close
                                        </Button>
                                    </span>
                                </span>
                            </MUITooltip>
                        )}
                    </div>
                );
            },
        },
    ];

    const rows =
        listingsLocal?.map((listing, index) => ({
            id: listing.listingKey || index,
            listing: listing,
            type: listing.attributes?.useType,
            askingRentMax: findPriceOrRate(listing),
        })) || [];

    return (
        <div>
            <StyledDataGrid
                columns={columns}
                rows={rows}
                autoHeight
                getRowHeight={() => "auto"}
                showToolbar
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                paginationModel={{ page: currentPage - 1, pageSize }}
                onPaginationModelChange={(model) => setCurrentPage(model.page + 1)}
                pageSizeOptions={[pageSize]}
                hideFooter
                slots={{
                    toolbar: BrokerActiveListingsToolbar,
                }}
                slotProps={{
                    toolbar: {
                        csvOptions: { disableToolbarButton: true },
                        printOptions: { disableToolbarButton: true },
                    },
                }}
            />
            <StyledPaginationContainer>
                <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                    variant="outlined"
                    shape="rounded"
                />
                <StyledPageInfo>
                    Showing {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, totalCount)} of{" "}
                    {totalCount} items
                </StyledPageInfo>
            </StyledPaginationContainer>
        </div>
    );
};

export default BrokerActiveListingsTable;
